import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Reveal } from "@/components/Reveal";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [pending, setPending] = useState(true);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setPending(false); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const res = await axios.get(`${API}/orders/by-session/${sessionId}`);
          setOrder(res.data);
          setPending(false);
          if (!cleared.current) { cleared.current = true; clear(); }
          return;
        }
      } catch { /* retry */ }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setPending(false);
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="bg-ivory min-h-[70vh]">
      <section className="mx-auto max-w-3xl px-6 sm:px-12 py-24 text-center">
        <Reveal>
          {pending ? (
            <>
              <p className="eyebrow">Confirming</p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl font-light tracking-tight">Lighting the wick…</h1>
              <p className="mt-6 text-sm text-stone2" data-testid="payment-confirming">We're confirming your payment. This takes just a moment.</p>
            </>
          ) : order ? (
            <>
              <p className="eyebrow">Order Confirmed</p>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl font-light tracking-tight" data-testid="order-confirmed-heading">Thank You, Beautifully.</h1>
              <p className="mt-6 text-sm md:text-base text-stone2 leading-relaxed">Your candles will be hand-poured, wrapped with care, and on their way soon. A receipt has been sent to your email.</p>
              <div className="mt-10 border border-line bg-white p-8 text-left" data-testid="order-summary">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Order Summary</p>
                <ul className="mt-4 divide-y divide-line">
                  {order.items.map((i) => (
                    <li key={i.slug} className="flex items-center justify-between py-3 text-sm">
                      <span>{i.name} <span className="text-stone2">· {i.size} × {i.quantity}</span></span>
                      <span className="font-medium">${(i.unit_price * i.quantity).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 flex items-center justify-between border-t border-line pt-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone2">Total</span>
                  <span className="font-serif text-2xl font-light" data-testid="order-total">${order.amount.toFixed(2)}</span>
                </div>
              </div>
              <Link to="/shop" data-testid="success-continue-shopping" className="btn-primary mt-10">Continue Shopping</Link>
            </>
          ) : (
            <>
              <h1 className="font-serif text-4xl font-light tracking-tight">We're checking on your order.</h1>
              <p className="mt-6 text-sm text-stone2">If you completed checkout, your confirmation will arrive by email shortly.</p>
              <Link to="/shop" className="btn-outline mt-10" data-testid="success-back-shop">Back to Shop</Link>
            </>
          )}
        </Reveal>
      </section>
    </div>
  );
};

export default PaymentSuccess;
